import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  ThemeProvider,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { type YouTubePlayerHandle } from '@/components/YouTubePlayer';
import RefereeEventTimeline from '@/components/review/RefereeEventTimeline';
import ReviewVideoPlayerShell from '@/components/review/ReviewVideoPlayerShell';
import DecisionFields, {
  emptyDecisionForm,
  eventToDecisionForm,
  isDecisionMarkReady,
  normalizeDecisionPayload,
  type DecisionFormValues,
} from '@/components/review/DecisionFields';
import DecisionFilterPanel from '@/components/review/DecisionFilterPanel';
import ReviewViewModeToolbar from '@/components/review/ReviewViewModeToolbar';
import OfficialSourceInline from '@/components/review/OfficialSourceInline';
import ConsensusRateInfoIcon from '@/components/review/ConsensusRateInfoIcon';
import {
  clusterMatchesFilters,
  eventMatchesFilters,
  type ActiveDecisionFilter,
  type DecisionFilterOption,
} from '@/components/review/decisionFilters';
import {
  buildConsensusClusters,
  buildClusterTimelineEvents,
  getTimelineMarkerIcon,
} from '@/components/review/consensus';
import type {
  ConsolidatedEventCluster,
  EventResponse,
  ReviewSession,
  SessionEvent,
} from '@/types';
import { formatDecisionSummary, formatVideoTime, PARTICIPANT_DEADLINE_MESSAGE } from '@/types';
import { useMobileLayout } from '@/hooks/useMobileLayout';
import { useAuth } from '@/hooks/useAuth';
import { createTheme } from '@mui/material/styles';
import { getTheme } from '@/theme';
import { proModeSurfaces, darkScrollbarSx } from '@/utils/themeSurfaces';
import { clampTimelineZoom } from '@/components/review/MatchEventHorizontalTimeline';
import {
  PRO_MODE_DIALOG_Z_INDEX,
  PRO_MODE_MENU_Z_INDEX,
  PRO_MODE_POPOVER_Z_INDEX,
  PRO_MODE_TOOLTIP_Z_INDEX,
  PRO_MODE_Z_INDEX,
} from '@/components/review/reviewZIndex';

type PanelMode = 'mark-official' | 'mark-participant' | 'respond';
const MAGNIFIER_ZOOM_MIN = 1.5;
const MAGNIFIER_ZOOM_MAX = 4;
const MAGNIFIER_ZOOM_STEP = 0.5;

function activeToolbarIconSx(active: boolean, color: string) {
  return active
    ? {
        color: 'secondary.main',
        bgcolor: alpha(color, 0.22),
        border: '1px solid',
        borderColor: alpha(color, 0.5),
        '&:hover': { bgcolor: alpha(color, 0.32) },
      }
    : {};
}

interface Props {
  open: boolean;
  session: ReviewSession;
  events: SessionEvent[];
  responses: EventResponse[];
  canMarkOfficial: boolean;
  canMarkParticipant: boolean;
  canEditAllEvents?: boolean;
  participantDeadlinePassed?: boolean;
  canViewOfficialMarks?: boolean;
  showConsensusView?: boolean;
  consensusViewReadOnly?: boolean;
  officialVsAllView?: boolean;
  canOpenEventStats?: boolean;
  canUseConsensusTimeline?: boolean;
  consensusTimeline?: boolean;
  onConsensusTimelineChange?: (enabled: boolean) => void;
  integrateOfficialMarks?: boolean;
  onIntegrateOfficialMarksChange?: (enabled: boolean) => void;
  currentUserId?: number;
  officialUserId?: number;
  officialUserIds?: number[];
  isOfficialSourceUser?: boolean;
  officialSourceDisplayName?: string;
  timelineScopeSuffix?: string;
  showOfficialSourceNotice?: boolean;
  showFacilitatorConsensusTimeline?: boolean;
  isComparisonMode?: boolean;
  initialTime?: number;
  initialPlaybackRate?: number;
  onClose: (resumeTime?: number, playbackRate?: number) => void;
  onMarkOfficial: (payload: ReturnType<typeof normalizeDecisionPayload>, videoSecond: number) => Promise<string | void>;
  onMarkParticipant: (payload: ReturnType<typeof normalizeDecisionPayload>, videoSecond: number) => Promise<string | void>;
  onRespond: (officialEventId: string, payload: ReturnType<typeof normalizeDecisionPayload>) => Promise<void>;
  onEditEvent: (eventId: string, payload: ReturnType<typeof normalizeDecisionPayload> & { video_second?: number }) => Promise<void>;
  onDeleteEvent: (eventId: string) => Promise<void>;
  educationMode?: boolean;
  canManageEducationMode?: boolean;
  onEducationModeChange?: (enabled: boolean) => void;
  decisionFilters?: ActiveDecisionFilter[];
  onDecisionFiltersChange?: (filters: ActiveDecisionFilter[]) => void;
  filterOptions?: DecisionFilterOption[];
}

function defaultMode(
  canMarkOfficial: boolean,
  canMarkParticipant: boolean,
  participantDeadlinePassed?: boolean
): PanelMode | null {
  if (canMarkParticipant && !participantDeadlinePassed) return 'mark-participant';
  if (canMarkOfficial) return 'mark-official';
  if (participantDeadlinePassed && canMarkParticipant) return 'mark-participant';
  return null;
}

export default function ReviewProMode({
  open,
  session,
  events,
  responses,
  canMarkOfficial,
  canMarkParticipant,
  canEditAllEvents = false,
  participantDeadlinePassed = false,
  canViewOfficialMarks = false,
  showConsensusView = false,
  consensusViewReadOnly = false,
  officialVsAllView = false,
  canOpenEventStats = false,
  canUseConsensusTimeline = false,
  consensusTimeline = false,
  onConsensusTimelineChange,
  integrateOfficialMarks = false,
  onIntegrateOfficialMarksChange,
  currentUserId,
  officialUserId,
  officialUserIds,
  isOfficialSourceUser = false,
  officialSourceDisplayName = '',
  timelineScopeSuffix = '',
  showOfficialSourceNotice = false,
  showFacilitatorConsensusTimeline = false,
  isComparisonMode = false,
  initialTime = 0,
  initialPlaybackRate = 1,
  onClose,
  onMarkOfficial,
  onMarkParticipant,
  onRespond,
  onEditEvent,
  onDeleteEvent,
  educationMode = false,
  canManageEducationMode = false,
  onEducationModeChange,
  decisionFilters = [],
  onDecisionFiltersChange,
  filterOptions = [],
}: Props) {
  const { user } = useAuth();
  const proTheme = useMemo(() => {
    const base = getTheme(user?.role, 'dark');
    return createTheme(base, {
      zIndex: {
        ...base.zIndex,
        tooltip: PRO_MODE_TOOLTIP_Z_INDEX,
      },
      components: {
        MuiPopover: {
          styleOverrides: {
            root: { zIndex: PRO_MODE_MENU_Z_INDEX },
          },
        },
        MuiMenu: {
          styleOverrides: {
            root: { zIndex: PRO_MODE_MENU_Z_INDEX },
          },
        },
        MuiTooltip: {
          defaultProps: {
            slotProps: {
              popper: { sx: { zIndex: PRO_MODE_TOOLTIP_Z_INDEX } },
            },
          },
        },
      },
    });
  }, [user?.role]);
  const surfaces = useMemo(() => proModeSurfaces(proTheme, { forceDark: true }), [proTheme]);
  const playerRef = useRef<YouTubePlayerHandle>(null);
  const { isMobile, isPortraitMobile, isLandscapeMobile } = useMobileLayout();
  const eventMap = useMemo(() => new Map(events.map((e) => [e.id, e])), [events]);
  const clusters = useMemo(
    () =>
      buildConsensusClusters(events, responses, {
        ownerUserIds:
          officialUserIds?.length
            ? officialUserIds
            : officialUserId != null
              ? [officialUserId]
              : [session.created_by_user_id],
      }),
    [events, responses, officialUserIds, officialUserId, session.created_by_user_id]
  );
  const ownMarkEvents = useMemo(
    () =>
      events
        .filter(
          (e) =>
            Number(e.created_by_user_id) === Number(currentUserId) &&
            (e.on_field_decision || e.judgement || e.note?.trim()) &&
            e.video_second != null
        )
        .sort((a, b) => (a.video_second ?? 0) - (b.video_second ?? 0)),
    [events, currentUserId]
  );

  const filteredClusters = useMemo(() => {
    if (decisionFilters.length === 0) return clusters;
    return clusters.filter((c) => clusterMatchesFilters(c, eventMap, decisionFilters));
  }, [clusters, decisionFilters, eventMap]);

  const filteredEvents = useMemo(() => {
    if (decisionFilters.length === 0) return events;
    return events.filter((e) => eventMatchesFilters(e, decisionFilters));
  }, [events, decisionFilters]);

  const displayClusters = decisionFilters.length > 0 ? filteredClusters : clusters;

  const displayOwnMarkEvents = useMemo(() => {
    if (decisionFilters.length === 0) return ownMarkEvents;
    const ids = new Set(filteredEvents.map((e) => e.id));
    return ownMarkEvents.filter((e) => ids.has(e.id));
  }, [ownMarkEvents, filteredEvents, decisionFilters.length]);

  const timelineEvents = useMemo(() => {
    const activeClusters = decisionFilters.length > 0 ? filteredClusters : clusters;
    if (showConsensusView) return buildClusterTimelineEvents(activeClusters, officialUserId);
    return displayOwnMarkEvents;
  }, [
    clusters,
    filteredClusters,
    displayOwnMarkEvents,
    showConsensusView,
    decisionFilters.length,
  ]);

  const timelineClusterById = useMemo(
    () => new Map(displayClusters.map((c) => [c.clusterId, c])),
    [displayClusters]
  );

  const resolveMarkerIcon = useCallback(
    (event: SessionEvent) =>
      getTimelineMarkerIcon(event, {
        clusterById: showConsensusView ? timelineClusterById : undefined,
        officialUserId,
      }),
    [showConsensusView, timelineClusterById, officialUserId]
  );

  const listItemCount = showConsensusView
    ? displayClusters.length
    : displayOwnMarkEvents.length;

  const [currentTime, setCurrentTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(initialPlaybackRate);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedClusterId, setSelectedClusterId] = useState<string | null>(null);
  const [mode, setMode] = useState<PanelMode | null>(
    defaultMode(canMarkOfficial, canMarkParticipant, participantDeadlinePassed)
  );
  const [respondEventId, setRespondEventId] = useState<string | null>(null);
  const [form, setForm] = useState<DecisionFormValues>(emptyDecisionForm());
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const [markFormCollapsed, setMarkFormCollapsed] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [magnifierEnabled, setMagnifierEnabled] = useState(false);
  const [magnifierZoom, setMagnifierZoom] = useState(MAGNIFIER_ZOOM_MIN);
  const [timelineZoom, setTimelineZoom] = useState(1);
  const wasPlayingBeforeMarkPanelRef = useRef(false);

  const showRightPanel = !rightPanelCollapsed;
  const landscapeMarkPanelOpen = isLandscapeMobile && showRightPanel && !markFormCollapsed;
  const showAnswers = !educationMode;

  const showViewModeToggles =
    canUseConsensusTimeline || canManageEducationMode || isComparisonMode;
  const canUseForm = canMarkOfficial || canMarkParticipant || participantDeadlinePassed;
  const autoCollapseMarkOnEducation =
    educationMode && canMarkOfficial && mode != null && mode !== 'respond';
  const usesSharedParticipantMarking = canMarkParticipant && !canMarkOfficial;
  const isParticipantOnly =
    usesSharedParticipantMarking || (participantDeadlinePassed && !canMarkOfficial);
  const showComparisonInList = isComparisonMode;
  const showEventList =
    (!landscapeMarkPanelOpen || markFormCollapsed) &&
    (!isMobile ||
      showComparisonInList ||
      usesSharedParticipantMarking ||
      canMarkOfficial ||
      canEditAllEvents ||
      showConsensusView);

  const resetToDefaultMode = useCallback(() => {
    setForm(emptyDecisionForm());
    setRespondEventId(null);
    setMode(defaultMode(canMarkOfficial, canMarkParticipant, participantDeadlinePassed));
  }, [canMarkOfficial, canMarkParticipant, participantDeadlinePassed]);

  const openMarkPanel = useCallback(() => {
    wasPlayingBeforeMarkPanelRef.current = isPlaying;
    playerRef.current?.pause();
    setSubmitError(null);
    resetToDefaultMode();
    setRightPanelCollapsed(false);
    setMarkFormCollapsed(false);
  }, [isPlaying, resetToDefaultMode]);

  const closeMarkPanel = useCallback((resumePlayback = true) => {
    setRightPanelCollapsed(true);
    if (resumePlayback && wasPlayingBeforeMarkPanelRef.current) {
      playerRef.current?.play();
    }
  }, []);

  const handleClose = useCallback(() => {
    const t = playerRef.current?.getCurrentTime() ?? currentTime;
    const rate = playerRef.current?.getPlaybackRate() ?? playbackRate;
    onClose(t, rate);
  }, [currentTime, playbackRate, onClose]);

  useEffect(() => {
    if (!open) return;
    setCurrentTime(initialTime);
    setPlaybackRate(initialPlaybackRate);
    setForm(emptyDecisionForm());
    setRespondEventId(null);
    setSelectedClusterId(null);
    setMode(defaultMode(canMarkOfficial, canMarkParticipant, participantDeadlinePassed));
    setRightPanelCollapsed(isLandscapeMobile);
    setMarkFormCollapsed(false);
    setSubmitError(null);
    setMagnifierEnabled(false);
    setMagnifierZoom(MAGNIFIER_ZOOM_MIN);
    setTimelineZoom(1);
  }, [
    open,
    initialTime,
    initialPlaybackRate,
    canMarkOfficial,
    canMarkParticipant,
    participantDeadlinePassed,
    isLandscapeMobile,
  ]);

  useEffect(() => {
    if (autoCollapseMarkOnEducation) {
      setMarkFormCollapsed(true);
    } else if (!educationMode || !consensusTimeline) {
      setMarkFormCollapsed(false);
    }
  }, [autoCollapseMarkOnEducation, educationMode, consensusTimeline]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inFormField = target?.closest(
        'input, textarea, select, [contenteditable="true"], .MuiTextField-root, .MuiSelect-root, .MuiToggleButtonGroup-root'
      );
      if (inFormField) return;

      if (e.code === 'Space') {
        e.preventDefault();
        playerRef.current?.togglePlay();
      }
      if (e.code === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, handleClose]);

  const handleFormChange = (field: keyof DecisionFormValues, value: string) => {
    setForm((v) => ({ ...v, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!mode) return;
    setSubmitError(null);
    const payload = normalizeDecisionPayload(form);
    const t = playerRef.current?.getCurrentTime() ?? currentTime;

    try {
      let selectId: string | void;
      if (mode === 'mark-official') {
        selectId = await onMarkOfficial(payload, t);
      } else if (mode === 'mark-participant') {
        selectId = await onMarkParticipant(payload, t);
      } else if (mode === 'respond' && respondEventId) {
        await onRespond(respondEventId, payload);
        selectId = undefined;
      } else {
        selectId = undefined;
      }
      if (selectId) setSelectedClusterId(selectId);
      resetToDefaultMode();
      if (isLandscapeMobile) {
        closeMarkPanel(true);
      }
    } catch {
      setSubmitError('無法儲存標記，請選擇判決評價後重試');
    }
  };

  const startRespond = (event: SessionEvent, clusterId?: string) => {
    setSelectedClusterId(clusterId || selectedClusterId);
    setRespondEventId(event.id);
    const existing = responses.find((r) => r.official_event_id === event.id && r.user_id === currentUserId);
    setForm(existing ? eventToDecisionForm(existing) : emptyDecisionForm());
    setMode('respond');
    if (event.video_second != null) playerRef.current?.seekTo(Math.max(0, event.video_second - 0.8));
  };

  useEffect(() => {
    if (!open) return;
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
    };
  }, [open]);

  if (!open) return null;

  const panelTitle =
    mode === 'respond'
      ? '作答判決'
      : mode === 'mark-official' || (mode === 'mark-participant' && isOfficialSourceUser)
        ? '新增官方判決標記'
        : mode === 'mark-participant'
          ? '新增判決標記'
          : '';

  const submitLabel =
    mode === 'respond'
      ? '提交作答'
      : `標記於 ${formatVideoTime(currentTime)}`;

  const headerTitle = `${session.title} — ${
    showConsensusView ? (officialVsAllView ? '官方比對' : '專業分析') : '全螢幕標記'
  }`;

  const closeButton = (
    <IconButton
      color="inherit"
      onClick={handleClose}
      aria-label="退出全螢幕"
      sx={isMobile ? { flexShrink: 0, ml: 'auto' } : undefined}
    >
      <CloseIcon />
    </IconButton>
  );

  const toolbarActions = (
    <>
      {showViewModeToggles && (
        <ReviewViewModeToolbar
          canUseConsensusTimeline={canUseConsensusTimeline}
          consensusTimeline={consensusTimeline}
          onConsensusTimelineChange={onConsensusTimelineChange}
          canManageEducationMode={canManageEducationMode}
          educationMode={educationMode}
          onEducationModeChange={onEducationModeChange}
          canIntegrateOfficialMarks={isComparisonMode}
          integrateOfficialMarks={integrateOfficialMarks}
          onIntegrateOfficialMarksChange={onIntegrateOfficialMarksChange}
          tone="dark"
        />
      )}
      <Tooltip title={magnifierEnabled ? '關閉放大鏡' : '放大鏡'}>
        <IconButton
          color={magnifierEnabled ? 'secondary' : 'inherit'}
          onClick={() => setMagnifierEnabled((v) => !v)}
          aria-label="放大鏡"
          aria-pressed={magnifierEnabled}
          sx={activeToolbarIconSx(magnifierEnabled, proTheme.palette.secondary.main)}
        >
          <SearchOutlinedIcon />
        </IconButton>
      </Tooltip>
      {magnifierEnabled && (
        <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0 }}>
          <IconButton
            size="small"
            color="inherit"
            onClick={() =>
              setMagnifierZoom((z) =>
                Math.max(MAGNIFIER_ZOOM_MIN, Math.round((z - MAGNIFIER_ZOOM_STEP) * 2) / 2)
              )
            }
            disabled={magnifierZoom <= MAGNIFIER_ZOOM_MIN}
            aria-label="降低放大倍率"
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption" sx={{ minWidth: 34, textAlign: 'center', fontWeight: 700 }}>
            {magnifierZoom.toFixed(1)}x
          </Typography>
          <IconButton
            size="small"
            color="inherit"
            onClick={() =>
              setMagnifierZoom((z) =>
                Math.min(MAGNIFIER_ZOOM_MAX, Math.round((z + MAGNIFIER_ZOOM_STEP) * 2) / 2)
              )
            }
            disabled={magnifierZoom >= MAGNIFIER_ZOOM_MAX}
            aria-label="提高放大倍率"
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Stack>
      )}
      {isLandscapeMobile && canUseForm ? (
        landscapeMarkPanelOpen ? (
          <Button size="small" variant="outlined" color="inherit" onClick={() => closeMarkPanel(true)}>
            關閉
          </Button>
        ) : (
          <Button size="small" variant="contained" color="secondary" onClick={openMarkPanel}>
            新增標記
          </Button>
        )
      ) : (
        <Tooltip title={rightPanelCollapsed ? '顯示標記面板' : '收合標記面板'}>
          <IconButton
            color="inherit"
            onClick={() => setRightPanelCollapsed((v) => !v)}
            aria-label={rightPanelCollapsed ? '顯示標記面板' : '收合標記面板'}
          >
            {rightPanelCollapsed ? <ViewSidebarIcon /> : <ViewSidebarOutlinedIcon />}
          </IconButton>
        </Tooltip>
      )}
    </>
  );

  return (
    <ThemeProvider theme={proTheme}>
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: PRO_MODE_Z_INDEX,
        bgcolor: surfaces.rootBg,
        display: 'flex',
        flexDirection: 'column',
        color: 'text.primary',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          borderBottom: '1px solid',
          borderColor: surfaces.border,
          bgcolor: surfaces.headerBg,
          pt: 'max(0px, env(safe-area-inset-top))',
          pl: 'max(0px, env(safe-area-inset-left))',
          pr: 'max(0px, env(safe-area-inset-right))',
        }}
      >
        {isMobile ? (
          <Stack spacing={0.75} sx={{ px: 1.5, py: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minHeight: 44, minWidth: 0 }}>
              <Typography
                fontWeight={700}
                sx={{ minWidth: 0, flex: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}
                noWrap
                title={headerTitle}
              >
                {headerTitle}
              </Typography>
              {closeButton}
            </Stack>
            <Box
              sx={{
                overflowX: 'auto',
                overflowY: 'hidden',
                WebkitOverflowScrolling: 'touch',
                mx: -0.5,
                px: 0.5,
                ...darkScrollbarSx(proTheme, { forceDark: true }),
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.75}
                useFlexGap
                sx={{ flexWrap: 'nowrap', width: 'max-content', pb: 0.25 }}
              >
                {toolbarActions}
              </Stack>
            </Box>
          </Stack>
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{ px: 2, py: 1, minHeight: 48 }}
          >
            <Typography
              fontWeight={700}
              sx={{
                minWidth: 0,
                flex: '1 1 12rem',
                maxWidth: 'min(48%, 420px)',
              }}
              noWrap
              title={headerTitle}
            >
              {headerTitle}
            </Typography>
            <Stack direction="row" alignItems="center" flexShrink={0} spacing={0.75} useFlexGap>
              {toolbarActions}
              {closeButton}
            </Stack>
          </Stack>
        )}
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: isPortraitMobile ? 'column' : 'row', minHeight: 0 }}>
        <Box
          sx={{
            flex: isPortraitMobile ? '0 0 auto' : 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            minHeight: 0,
          }}
        >
          <ReviewVideoPlayerShell
            playerRef={playerRef}
            videoId={session.youtube_video_id}
            playbackRate={playbackRate}
            initialTime={initialTime}
            currentTime={currentTime}
            videoDuration={videoDuration}
            isPlaying={isPlaying}
            timelineEvents={timelineEvents}
            selectedEventId={selectedClusterId}
            educationMode={educationMode}
            onTimeUpdate={setCurrentTime}
            onDurationReady={setVideoDuration}
            onPlayingChange={setIsPlaying}
            onSelectEvent={(e) => setSelectedClusterId(e.id)}
            onSeek={(s) => playerRef.current?.seekTo(s)}
            onTogglePlay={() => playerRef.current?.togglePlay()}
            onPlaybackRateChange={setPlaybackRate}
            layout="flex"
            forceDark
            magnifierEnabled={magnifierEnabled}
            magnifierZoom={magnifierZoom}
            onMagnifierZoomChange={setMagnifierZoom}
            magnifierZoomMin={MAGNIFIER_ZOOM_MIN}
            magnifierZoomMax={MAGNIFIER_ZOOM_MAX}
            magnifierZoomStep={MAGNIFIER_ZOOM_STEP}
            timelineZoom={timelineZoom}
            onTimelineZoomChange={(z) => setTimelineZoom(clampTimelineZoom(z))}
            resolveMarkerIcon={resolveMarkerIcon}
            hideBottomTimeline={isLandscapeMobile}
          />
        </Box>

        {showRightPanel && (
          <Box
            sx={{
              width: isPortraitMobile ? '100%' : isLandscapeMobile ? 300 : 400,
              borderLeft: isPortraitMobile ? 'none' : '1px solid',
              borderTop: isPortraitMobile ? '1px solid' : 'none',
              borderColor: surfaces.border,
              display: 'flex',
              flexDirection: 'column',
              flex: isPortraitMobile ? 1 : undefined,
              flexShrink: isPortraitMobile ? 1 : 0,
              bgcolor: surfaces.panelBg,
              minHeight: 0,
              overflow: isMobile ? 'auto' : undefined,
              WebkitOverflowScrolling: 'touch',
              ...darkScrollbarSx(proTheme, { forceDark: true }),
            }}
          >
            {canUseForm && mode && (
              <Box
                sx={{
                  flexShrink: 0,
                  borderBottom: markFormCollapsed ? 'none' : '1px solid',
                  borderColor: surfaces.border,
                  width: '100%',
                }}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={0.5}
                  sx={{
                    px: isLandscapeMobile ? 1.5 : 2,
                    py: 1,
                    minHeight: 44,
                    cursor:
                      autoCollapseMarkOnEducation && markFormCollapsed ? 'default' : 'pointer',
                  }}
                  onClick={() => {
                    if (markFormCollapsed) {
                      if (!autoCollapseMarkOnEducation) setMarkFormCollapsed(false);
                    } else {
                      setMarkFormCollapsed(true);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-expanded={!markFormCollapsed}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      if (markFormCollapsed) {
                        if (!autoCollapseMarkOnEducation) setMarkFormCollapsed(false);
                      } else {
                        setMarkFormCollapsed(true);
                      }
                    }
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={700} sx={{ minWidth: 0 }} noWrap>
                    {panelTitle}
                  </Typography>
                  <Tooltip title={markFormCollapsed ? '展開標記面板' : '收合標記面板'}>
                    <span>
                      <IconButton
                        size="small"
                        color="inherit"
                        disabled={autoCollapseMarkOnEducation && markFormCollapsed}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (markFormCollapsed) {
                            if (!autoCollapseMarkOnEducation) setMarkFormCollapsed(false);
                          } else {
                            setMarkFormCollapsed(true);
                          }
                        }}
                        aria-label={markFormCollapsed ? '展開標記面板' : '收合標記面板'}
                      >
                        {markFormCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                {!markFormCollapsed && (
                  <Box
                    sx={{
                      px: isLandscapeMobile ? 1.5 : 2,
                      pb: isLandscapeMobile ? 1.5 : 2,
                      flex: landscapeMarkPanelOpen ? 1 : undefined,
                      overflow: landscapeMarkPanelOpen ? 'auto' : undefined,
                      minHeight: 0,
                      ...darkScrollbarSx(proTheme, { forceDark: true }),
                    }}
                  >
                    {submitError && (
                      <Typography variant="caption" color="error.light" sx={{ display: 'block', mb: 1 }}>
                        {submitError}
                      </Typography>
                    )}

                    {participantDeadlinePassed && mode === 'mark-participant' ? (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        {PARTICIPANT_DEADLINE_MESSAGE}
                      </Typography>
                    ) : (
                      <Stack spacing={isMobile ? 1 : 1.5}>
                        <DecisionFields
                          values={form}
                          onChange={handleFormChange}
                          dark={surfaces.isDark}
                          compact={isMobile}
                          menuZIndex={PRO_MODE_MENU_Z_INDEX}
                          dialogZIndex={PRO_MODE_DIALOG_Z_INDEX}
                        />

                        <Stack direction="row" spacing={1}>
                          {mode === 'respond' && (
                            <Button variant="outlined" onClick={resetToDefaultMode}>
                              取消
                            </Button>
                          )}
                          {landscapeMarkPanelOpen && mode !== 'respond' && (
                            <Button variant="outlined" onClick={() => closeMarkPanel(true)}>
                              取消
                            </Button>
                          )}
                          <Button
                            variant="contained"
                            color="secondary"
                            fullWidth
                            disabled={!isDecisionMarkReady(form)}
                            onClick={handleSubmit}
                          >
                            {submitLabel}
                          </Button>
                        </Stack>
                      </Stack>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {showEventList && (
              <Box
                sx={{
                  flex: 1,
                  overflow: 'auto',
                  p: 2,
                  minHeight: 0,
                  width: '100%',
                  boxSizing: 'border-box',
                  ...darkScrollbarSx(proTheme, { forceDark: true }),
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  sx={{ mb: 1, width: '100%' }}
                >
                  <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={0.25} flexWrap="wrap">
                      <Typography variant="subtitle1" fontWeight={700}>
                        {showComparisonInList
                          ? '判決時間軸（與官方比對）'
                          : `判決時間軸${timelineScopeSuffix || (showConsensusView ? '（整合全體回覆）' : '（我的）')}`}
                        {decisionFilters.length > 0 && (
                          <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            ({listItemCount} 項)
                          </Typography>
                        )}
                      </Typography>
                      {showConsensusView && !showComparisonInList && <ConsensusRateInfoIcon />}
                    </Stack>
                    {showOfficialSourceNotice && showFacilitatorConsensusTimeline && officialSourceDisplayName && (
                      <OfficialSourceInline
                        displayName={officialSourceDisplayName}
                        dense
                        tone="dark"
                      />
                    )}
                  </Stack>
                  {filterOptions.length > 0 && onDecisionFiltersChange && (
                    <DecisionFilterPanel
                      options={filterOptions}
                      active={decisionFilters}
                      onChange={onDecisionFiltersChange}
                      popoverZIndex={PRO_MODE_POPOVER_Z_INDEX}
                    />
                  )}
                </Stack>
                <RefereeEventTimeline
                  fullWidth
                  events={events}
                  responses={responses}
                  clusters={displayClusters}
                  canEditOfficial={canMarkOfficial}
                  canEditAllEvents={canEditAllEvents}
                  canViewEventStats={canOpenEventStats}
                  canEditOwnMarks={usesSharedParticipantMarking || isParticipantOnly}
                  canRespond={canMarkParticipant && !participantDeadlinePassed}
                  selectedEventId={selectedClusterId}
                  onSelect={(e) => setSelectedClusterId(e.id)}
                  onSeek={(s) => playerRef.current?.seekTo(s)}
                  onPlay={() => playerRef.current?.play()}
                  onPause={() => playerRef.current?.pause()}
                  onEditSubmit={async (event, values) => onEditEvent(event.id, values)}
                  onDelete={(e) => onDeleteEvent(e.id)}
                  onRespond={startRespond}
                  currentUserId={currentUserId}
                  officialSourceUserId={officialUserId}
                  officialSourceUserIds={officialUserIds}
                  timelineScopeSuffix={timelineScopeSuffix}
                  showConsensusView={showConsensusView}
                  consensusViewReadOnly={consensusViewReadOnly}
                  showParticipantMarkers={showConsensusView && !showFacilitatorConsensusTimeline}
                  participantComparisonView={isComparisonMode}
                  officialVsAllView={officialVsAllView}
                  ownParticipantMarksOnly={!showConsensusView}
                  hideTitle
                  educationMode={educationMode}
                  videoDuration={videoDuration}
                  popoverZIndex={PRO_MODE_POPOVER_Z_INDEX}
                  menuZIndex={PRO_MODE_MENU_Z_INDEX}
                  dialogZIndex={PRO_MODE_DIALOG_Z_INDEX}
                  participantDeadlinePassed={participantDeadlinePassed}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
    </ThemeProvider>
  );
}
